<template>
    <x-form ref="productInformationForm" @submit.prevent="submit">
        <x-container fluid>
            <template v-if="loading">
                <x-layout>
                    <x-flex xs12>
                        <loading-spinner class="compact"/>
                    </x-flex>
                </x-layout>
            </template>

            <template v-else>
                <x-layout class="question-set">
                    <x-flex xs2 offset-xs1>
                        <h3 class="question-set-title">Policy type</h3>
                    </x-flex>

                    <x-flex xs6 class="question-set-questions">
                        <x-container>
                            <x-layout v-show="showField('riskStructure')">
                                <x-flex xs12>
                                    <x-radio-group
                                        v-model="riskStructure"
                                        :row="true"
                                        class="required"
                                        data-test="risk_structure"
                                        :rules="[
                                            v => ruleRequired(v, 'Risk Structure')
                                        ]"
                                    >
                                        <template v-slot:label>
                                            Risk Structure
                                        </template>

                                        <x-radio v-for="riskStructureType in riskStructureTypes" :key="riskStructureType.value" :label="riskStructureType.name" :value="riskStructureType.value" />
                                    </x-radio-group>
                                </x-flex>
                            </x-layout>

                            <template v-if="submission.risk_structure === 'accepted_declaration'">
                                <x-layout v-if="showField('enquiryType')">
                                    <x-flex xs12>
                                        <x-radio-group
                                            v-model="submission.is_manual_firm_order"
                                            :row="true"
                                            class="required"
                                            data-test="enquiry_type"
                                            :rules="[
                                                v => ruleRequired(v, 'Enquiry type')
                                            ]"
                                            :disabled="isConvertingFromManualSubmission"
                                        >
                                            <template v-slot:label>
                                                Enquiry type
                                            </template>

                                            <x-radio v-for="enquiryType in enquiryTypes" :key="'enquiry-type-' + enquiryType.value" :label="enquiryType.name" :value="enquiryType.value" />
                                        </x-radio-group>
                                    </x-flex>
                                </x-layout>
                            </template>
                        </x-container>
                    </x-flex>
                </x-layout>

                <template v-if="submission.risk_structure === 'accepted_declaration'">
                    <x-layout v-if="showField('client') || showField('clientReference')" class="question-set">
                        <x-flex xs2 offset-xs1>
                            <h3 class="question-set-title">Policy</h3>
                        </x-flex>

                        <x-flex xs6 class="question-set-questions">
                            <x-container>
                                <x-layout>
                                    <x-flex xs6 v-if="showField('client')">
                                        <x-autocomplete
                                            v-model="submission.client_company_id"
                                            :items="clients"
                                            class="required"
                                            label="Choose a client"
                                            placeholder="Client"
                                            item-value="id"
                                            item-text="name"
                                            data-test="client"
                                            :rules="[v => ruleRequired(v, 'Client')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                        />
                                    </x-flex>

                                    <x-flex v-if="showField('clientReference')" xs6>
                                        <x-text-field
                                            v-model="submission.client_reference"
                                            label="Client reference (if applicable)"
                                            placeholder="Client reference"
                                            outline
                                            maxlength="50"
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex v-if="showField('placingBrokerContact')" xs6>
                                        <x-autocomplete
                                            v-model="submission.placing_broker_contact_id"
                                            :items="filteredClientAssignments"
                                            class="required"
                                            label="Placing broker contact"
                                            placeholder=" "
                                            :rules="[v => ruleRequired(v, 'Placing broker contact')]"
                                            append-icon="keyboard_arrow_down"
                                            item-value="broker_user_id"
                                            item-text="user_name"
                                            data-test="placing_broker"
                                            outline
                                            required
                                            :menu-props="{ offsetY: true }"
                                        />
                                    </x-flex>

                                    <x-flex v-if="showField('facility')" xs6>
                                        <x-select
                                            v-model="submission.facility_id"
                                            :items="filteredFacilities"
                                            class="required"
                                            label="Choose facility"
                                            placeholder="Facility"
                                            item-value="id"
                                            item-text="name"
                                            data-test="facility"
                                            :rules="[v => ruleRequired(v, 'Facility')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                        />

                                        <facility-inactive-alert :show="!isFacilityActive" />
                                    </x-flex>
                                </x-layout>

                                <x-layout v-if="showField('entity')">
                                    <x-flex v-if="showField('entity')" xs6>
                                        <entity-field data-test="entity" :entities="filteredEntities" :submission="submission" />
                                    </x-flex>

                                    <x-flex v-show="showField('category')" xs6>
                                        <x-autocomplete
                                            v-model="submission.category_id"
                                            :items="filteredCategories"
                                            class="required"
                                            label="Choose a category"
                                            placeholder="Category"
                                            item-value="id"
                                            item-text="name"
                                            :rules="[v => ruleRequired(v, 'Category')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex v-show="showField('product')" xs6>
                                        <x-autocomplete
                                            v-model="submission.product_id"
                                            :items="filteredProducts"
                                            class="required"
                                            label="Choose a product"
                                            placeholder="Product"
                                            item-value="id"
                                            item-text="name"
                                            :rules="[v => ruleRequired(v, 'Product')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                        />
                                    </x-flex>

                                    <x-flex v-if="showField('currency')" xs6>
                                        <x-autocomplete
                                            v-model="submission.currency_id"
                                            :items="currencies"
                                            class="required"
                                            label="Currency"
                                            placeholder="Currency"
                                            item-value="id"
                                            item-text="name"
                                            :rules="[v => ruleRequired(v, 'Currency')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                            :disabled="isInvoice"
                                            :menu-props="{ offsetY: true }"
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex v-if="showField('captiveInsurer')" xs6>
                                        <x-select
                                            v-model="submission.captive_insurer_company_id"
                                            :items="filteredCaptiveInsurers"
                                            class="required"
                                            label="Captive insurer"
                                            placeholder="Captive insurer"
                                            item-value="id"
                                            item-text="name"
                                            :rules="[v => ruleRequired(v, 'Captive insurer')]"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                            readonly
                                        />
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-flex>
                    </x-layout>

                    <editable-generic-questions
                        v-show="showField('editableQuestions')"
                        v-model="submission"
                        :products-prop="products"
                        :currencies="currencies"
                        :clientAssignments="filteredClientAssignments"
                    />
                </template>

                <template v-else-if="submission.risk_structure === 'standalone_policy'">
                    <standalone-policy-questions
                        :submission="submission"
                        :currencies="currencies"
                        :countries="countries"
                        :categories="categories"
                        :products="products"
                        :placingBrokers="placing_brokers"
                        :clientAssignments="filteredClientAssignments"
                    />
                </template>

                <x-layout v-if="errors.length > 0" align-center>
                    <x-flex xs12>
                        <x-alert
                            :value="true"
                            danger
                            v-for="error in errors"
                            v-bind:key="error"
                        >
                            {{ error }}
                        </x-alert>
                    </x-flex>
                </x-layout>

                <x-layout class="action-set" align-center>
                    <x-flex xs6 offset-xs3 class="action-set-actions">
                        <x-container>
                            <x-layout>
                                <x-flex xs6>
                                    <x-btn @click="$emit('submission:cancel')" color="tertiary" class="mr-4">
                                        <i class="material-icons mr-2">arrow_back</i>

                                        {{ submission.id > 0 ? 'Back' : 'Cancel' }}
                                    </x-btn>

                                    <delete-quote-button v-if="submission.status === 'draft'" :submissionId="submission.id" />
                                </x-flex>

                                <x-flex xs6 text-xs-right>
                                    <x-btn
                                        class="wide show"
                                        :class="{ show: showField('nextButton') }"
                                        type="submit"
                                        color="primary"
                                        data-test="next"
                                        :disabled="loading"
                                    >
                                        Next

                                        <i class="material-icons ml-2">trending_flat</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>
            </template>
        </x-container>

        <modal
            ref="sofConfirmation"
            actionText="Yes"
            confirmation
        >
            <p>You are selecting a new risk structure - all previous answers will be lost</p>
        </modal>
    </x-form>
</template>

<script>
    import LoadingSpinner from '~/components/Shared/UIComponents/LoadingSpinner';
    import EditableGenericQuestions from '~/components/Broker/SubmissionWizard/EditableGenericQuestions.vue';
    import StandalonePolicyQuestions from '~/components/Broker/SubmissionWizard/StandalonePolicyQuestions.vue';
    import EntityField from '~/components/Broker/UIComponents/EntityField';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import FacilityInactiveAlert from '~/components/Broker/Facility/InactiveAlert';
    import DeleteQuoteButton from '~/components/Broker/UIComponents/DeleteQuoteButton';
    import rules from '~/mixins/rules';
    import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

    export default {
        name: 'ProductInformation',
        components: {
            LoadingSpinner,
            EditableGenericQuestions,
            StandalonePolicyQuestions,
            FacilityInactiveAlert,
            EntityField,
            Modal,
            DeleteQuoteButton,
        },
        mixins: [rules, submissionQuoteHelpers],
        props: ['submission', 'quoteTypes', 'facilities', 'currencies', 'info', 'role', 'products', 'currentStep', 'isConvertingFromManualSubmission'],
        data() {
            return {
                loading: true,
                riskStructure: undefined,
                showRiskStructureModal: true,
                companies: [],
                categories: [],
                placing_brokers: [],
                clientAssignments: [],
                enquiryTypes: [
                    {
                        value: 0,
                        name: 'Market enquiry'
                    },
                    {
                        value: 1,
                        name: 'Manual firm order'
                    }
                ],
                fields: [
                    {
                        key: 'riskStructure',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.risk_structure);
                        }
                    },
                    {
                        key: 'quoteType',
                        includeInForm(context) {
                            return context.role === 'placing_broker' || context.role === 'producing_broker';
                        },
                        isComplete(context) {
                            return Boolean(context.submission.quote_type_id);
                        }
                    },
                    {
                        key: 'enquiryType',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.is_manual_firm_order) || context.submission.is_manual_firm_order === 0;
                        }
                    },
                    {
                        key: 'client',
                        includeInForm(context) {
                            return context.role === 'placing_broker' || context.role === 'producing_broker';
                        },
                        isComplete(context) {
                            return Boolean(context.submission.client_company_id);
                        }
                    },
                    {
                        key: 'clientReference',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return true;
                        }
                    },
                    {
                        key: 'placingBrokerContact',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return true;
                        }
                    },
                    {
                        key: 'facility',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.facility_id);
                        }
                    },
                    {
                        key: 'entity',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return true;
                        }
                    },
                    {
                        key: 'category',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.category_id);
                        }
                    },
                    {
                        key: 'product',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.product_id);
                        }
                    },
                    {
                        key: 'currency',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.product_id);
                        }
                    },
                    {
                        key: 'captiveInsurer',
                        includeInForm(context) {
                            return context.selectedFacilityHasCaptiveInsurer;
                        },
                        isComplete(context) {
                            return Boolean(context.submission.captive_insurer_company_id);
                        }
                    },
                    {
                        key: 'editableQuestions',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            const productHasNoObligor = (typeof (context.products.find(p => p.id === context.submission.product_id && p.has_no_obligor)) !== 'undefined');

                            if (productHasNoObligor) {
                                return Boolean(context.submission.currency_id);
                            }

                            return Boolean(context.submission.obligors[0].pivot.obligor_id);
                        }
                    },
                    {
                        key: 'nextButton',
                        includeInForm(context) {
                            return true;
                        },
                        isComplete(context) {
                            return true;
                        }
                    }
                ],
                errors: [],
                countries: [],
            }
        },
        computed: {
            filteredQuoteTypes() {
                return this.quoteTypes.filter(qt => qt.key !== 'invoice' && qt.key !== 'standalone_policy');
            },
            producingBrokers() {
                return this.companies.filter(c => c.company_type === 'producing_broker');
            },
            placingBrokers() {
                return this.companies.filter(c => c.company_type === 'placing_broker');
            },
            captiveInsurers() {
                return this.companies.filter(c => c.company_type === 'captive_insurer');
            },
            selectedFacilityHasCaptiveInsurer() {
                return Boolean(this.selectedFacility?.captive_insurer_company_id);
            },
            filteredCaptiveInsurers() {
                if (this.selectedFacilityHasCaptiveInsurer) {
                    return [this.selectedFacility.captive_insurer_company];
                }
            },
            filteredPlacingBrokers() {
                if (this.role !== 'placing_broker') {
                    return this.placingBrokers;
                }
                return this.placingBrokers.filter(broker => broker.id === this.$root.user.company_id);

            },
            filteredProducingBrokers() {
                if (this.role !== 'producing_broker') {
                    return [
                        {
                            id: null,
                            name: 'N/A'
                        },
                        ...this.producingBrokers
                    ];
                }
                return this.producingBrokers.filter(producingBroker => producingBroker.id === this.$root.user.company_id);
            },
            filteredClientAssignments() {
                return this.clientAssignments.filter(broker => broker.client_company.id === this.submission.client_company_id);
            },
            hasObligor() {
                return !this.hasNoObligor;
            },
            filteredFacilities() {
                const clientCompanyId = this.role === 'client' ? this.$root.user.company.id : this.submission.client_company_id;
                return [
                    ...this.facilities.filter(
                        facility => {
                            const facilityHasSelectedClient = facility.client_company.id === clientCompanyId &&
                                (facility.status === 1 || facility.id === this.submission.facility_id)
                            const isValidCommitmentFacility = this.submission.quote_type?.key !== 'commitment' || !facility.captive_insurer_company;

                            return facilityHasSelectedClient && isValidCommitmentFacility;
                        }
                    )
                ];
            },
            isFacilityActive() {
                return this.$route.params.id !== 'new' ? this.filteredFacilities.find(
                        facility => facility.id === this.submission.facility_id
                    )?.status === 1 : true;
            },
            isInvoice() {
                return this.submission.quote_type?.key === 'invoice';
            },
            visibleFields() {
                const visibleFields = [];

                // If page has finished loading, loop through each field and check if it should be shown.
                // If it will be shown in form, add field to list of visible fields and check if it has been completed.
                // If it has not been completed, break the loop, which hides all following fields.
                if (!this.loading && Boolean(this.role)) {
                    this.fields.every(field => {
                        if (field.includeInForm(this)) {
                            visibleFields.push(field.key);
                            return field.isComplete(this);
                        }

                        return true;
                    });
                }

                return visibleFields;
            },
            riskStructureTypes() {
                let riskStructureTypes = [];

                if (!(this.submission.is_known_facility === false)) {
                    riskStructureTypes.push(
                        {
                            value: 'accepted_declaration',
                            name: 'Accepted Declaration',
                        }
                    );
                } else {
                    this.submission.risk_structure = 'standalone_policy';
                    this.riskStructure = 'standalone_policy';
                }

                if (this.role !== 'client') {
                    riskStructureTypes.push(
                        {
                            value: 'standalone_policy',
                            name: 'Standalone Policy'
                        }
                    );
                }
                return riskStructureTypes;
            }
        },
        watch: {
            role() {
                this.applyUserCompany();
            },
            'submission.quote_type_id'(newValue) {
                this.submission.quote_type = this.quoteTypes.find(quoteType => quoteType.id === newValue);

                // If facility ID is not in filteredFacilities after quote type change, set facility ID to null.
                // This is temporary while Captives is hidden for Capacity Reservation.
                if (this.submission.facility_id && !this.filteredFacilities.find(facility => facility.id === this.submission.facility_id)) {
                    this.submission.facility_id = null;
                }
            },
            'submission.product_id'(newValue) {
                this.submission.product = this.products.find(product => product.id === newValue);
            },
            filteredPlacingBrokers(newValue) {
                if (newValue.length === 1) {
                    this.submission.placing_broker_company_id = newValue[0].id;
                }
            },
            filteredProducingBrokers(newValue) {
                if (newValue.length === 1) {
                    this.submission.producing_broker_company_id = newValue[0].id;
                }
            },
            'submission.facility_id'(facilityId) {
                const selectedFacility = this.facilities.find(facility => facility.id === facilityId);

                if (!this.filteredCategories.some(category => category.id === this.submission.category_id)) {
                    this.submission.category_id = null;
                    this.submission.product_id = null;
                }

                if (!this.isDeclaration && selectedFacility) {
                    if (selectedFacility.producing_broker_company === null) {
                        this.submission.producing_broker_company_id = null;
                    } else {
                        this.submission.producing_broker_company_id = selectedFacility.producing_broker_company.id;
                    }

                    if (selectedFacility.captive_insurer_company === null) {
                        this.submission.captive_insurer_company_id = null;
                    } else {
                        this.submission.captive_insurer_company_id = selectedFacility.captive_insurer_company.id;
                    }

                    this.submission.placing_broker_company_id = selectedFacility.placing_broker_company.id;
                } else if (!this.isDeclaration && !selectedFacility) {
                    this.submission.captive_insurer_company_id = null;
                    this.submission.producing_broker_company_id = undefined;
                    this.submission.entity_id = null;
                }
            },
            'submission.risk_structure'(riskStructure) {
                if (riskStructure === 'accepted_declaration') {
                    this.submission.quote_type_id = this.filteredQuoteTypes.find(({key}) => key === 'open_marketplace')?.id;
                } else {
                    this.submission.quote_type_id = undefined;
                }
            },
            async riskStructure(newRiskStructure, oldRiskStructure) {
                if (!this.showRiskStructureModal) {
                    this.showRiskStructureModal = true;
                    return;
                }

                if (!oldRiskStructure) {
                    this.$emit('submission:reset-structure', newRiskStructure);
                    return;
                }

                if (await this.$refs.sofConfirmation.open()) {
                    this.$emit('submission:reset-structure', newRiskStructure);
                } else {
                    this.showRiskStructureModal = false;
                    this.riskStructure = oldRiskStructure;
                }
            },
        },
        created() {
            if (this.submission.risk_structure) {
                this.showRiskStructureModal = false;
                this.riskStructure = this.submission.risk_structure;
            }
        },
        mounted() {
            const mountRequests = [];
            mountRequests.push(axios.get(this.$root.api('company')).then(res => {
                this.companies = res.data.data
            }));
            mountRequests.push(axios.get(this.$root.api('categories')).then(res => {
                this.categories = res.data.data
            }));
            mountRequests.push(axios.get(this.$root.api(`country`)).then(res => {
                this.countries = res.data.data;
            }));
            mountRequests.push(axios.get(this.$root.api('broker')).then(res => {
                this.placing_brokers = res.data.data;

                if (this.placing_brokers.length === 1 && !this.submission.placing_broker_company_id) {
                    this.submission.placing_broker_company_id = this.placing_brokers[0].id;
                }
            }));
            mountRequests.push(axios.get(this.$root.api(`broker/client-assignment/placing-brokers`)).then(res => {
                    this.clientAssignments = res.data;
            }));

            axios.all(mountRequests).then(() => {
                this.loading = false;
                this.applyUserCompany();
            });
        },
        methods: {
            isNewSubmission() {
                return this.submission.id < 0;
            },
            applyUserCompany() {
                switch (this.role) {
                    case 'placing_broker':
                        this.submission.placing_broker_company_id = this.$root.user.company_id;
                        break;
                    case 'producing_broker':
                        this.submission.producing_broker_company_id = this.$root.user.company_id;
                        break;
                    case 'client':
                        this.submission.client_company_id = this.$root.user.company_id;
                        this.submission.quote_type_id = this.quoteTypes.find(qt => qt.key === 'open_marketplace').id;
                        this.submission.risk_structure = 'accepted_declaration';
                        this.riskStructure = 'accepted_declaration';
                        break;
                    default:
                        // user hasn't matched any role type
                        break;
                }
            },
            showField(fieldKey) {
                if (fieldKey === 'nextButton' && this.submission.risk_structure === 'standalone_policy') {
                    return true;
                }
                return this.visibleFields.indexOf(fieldKey) > -1;
            },
            submit() {
                if (this.$refs.productInformationForm.validate()) {
                    if (this.isNewSubmission() && this.submission.risk_structure !== 'standalone_policy') {
                        const selectedProduct = this.products.find(p => p.id === this.submission.product_id);

                        for (const [field, defaultValue] of Object.entries(selectedProduct.defaults)) {
                            this.submission[field] = defaultValue;
                        }
                    }

                    this.$emit('submission:navigate-to', this.currentStep + 1);
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    button[type=submit] {
        opacity: 0;
        pointer-events: none;

        &.show {
            opacity: 1;
            pointer-events: auto;
        }
    }
</style>
